import {
    faBan,
    faBars,
    faBell,
    faBellSlash,
    faCheck,
    faClipboardList,
    faClone,
    faCommentDots,
    faDownload,
    faEnvelope,
    faFileAlt,
    faFilePdf,
    faFileWord,
    faGavel,
    faLink,
    faMagic,
    faWrench,
    faRedo,
    faSync,
    faTrash,
    faUnlink,
    faUpload,
} from '@fortawesome/free-solid-svg-icons';

/**
 * Different states for a single handbook.
 * NOTE: These include the color and text property.
 *       - color: to determine the corresponding hex value from the theme's color pallette
 *       - text : to render the user friendly version of the state
 *
 * @type {Object}
 */
export const HANDBOOK_STATES = {
    FEDERAL: {
        color: 'federal',
        text: 'Federal',
    },
    MULTI_STATE: {
        color: 'multistate',
        text: 'Multi State',
    },
    ONE_STATE: {
        color: 'onestate',
        text: 'One State',
    },
};

/**
 * Map of all available addtional handbook options
 *
 * @type {Object}
 */
export const HANDBOOK_OPTIONS = {
    HANDBOOKS_ELITE: 'Handbooks Elite',
    MULTILINGUAL_HANDBOOKS: 'Multilingual Handbooks',
    MULTI_STATE_HANDBOOK: 'Multi-state Handbook',
    HANDBOOK_ELITE_PLUS: 'Handbook Elite Plus',
};

export const HANDBOOK_BLOCKED_POPUP = {
    BLOCKED_POPUP: {
        MESSAGE:
            'Looks like your browser is blocking popups. Please disable your popup blocker to allow for future downloads.',
        LABEL_BUTTON: 'Click here to download your handbook',
        TITLE: 'Download Popup Blocked',
    },
};

export const STATE_HASH = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District Of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
};

export const ALERT_CONTENT =
    'Policy alerts are present and pending on this handbook. Please accept/dismiss the policy alerts through the Table of Contents to enable handbook downloading.';
export const ALERT_CONTENT_LINKED =
    'The handbook cannot be downloaded because there are some policy alerts still pending to be reviewed in the handbook template. You can send a reminder to the administrator.';

export const HANDBOOK_COPY_MESSAGE = {
    DESCRIPTION:
        'Handbook Name already exists. Please choose a different name or use the suggested Handbook Name.',
    SUGGESTED_NAME: 'Suggested Name',
};

export const HANDBOOK_SUGGESTED_NAME_MESSAGE = {
    DESCRIPTION:
        'This handbook name already exists in the Handbook Library. Rename the handbook or use the name suggested below, then select Accept.',
    SUGGESTED_NAME: 'Suggested Name',
};

export const HANDBOOK_TRANSLATE_MESSAGE =
    'This handbook is currently being translated and will be available to download once complete.';

export const HANDBOOK_STATELIST_OPTION = 'Please pick the state you would like to download.';

export const TOC_MESSAGES = {
    emptyPoliciesList: "Drag and Drop a policy here using the 'Reorder' mode.",
    emptyCustomPoliciesList:
        "All custom policies have been assigned to sections in the Table of Contents. Drag and drop a custom policy here using the 'Reorder' mode.",
};

// A prefix to identify a policy section.
export const POLICY_SECTION_PREFIX = 'policy_section___';

// A prefix to identify a policy of type 'core'.
export const CORE_POLICY_PREFIX = 'policy___core___';

// A prefix to identify a policy of type 'custom'.
export const CUSTOM_POLICY_PREFIX = 'policy___custom___';

// A prefix to identify a policy of an 'unknown' type.
export const POLICY_PREFIX = 'policy___';

// A prefix to identify a policy of type 'federal'.
export const FEDERAL_POLICY_PREFIX = 'policy___federal___';

// A prefix to identify a policy that is 'locked'.
export const LOCKED_POLICY_PREFIX = 'policy___locked___';

// A prefix to identify a policy of type 'state'.
export const STATE_POLICY_PREFIX = 'policy___state___';

// A map of prefixes to identify multiple policy types.
export const POLICY_PREFIX_MAP = {
    core: CORE_POLICY_PREFIX,
    custom: CUSTOM_POLICY_PREFIX,
    default: POLICY_PREFIX,
    federal: FEDERAL_POLICY_PREFIX,
    locked: LOCKED_POLICY_PREFIX,
    state: STATE_POLICY_PREFIX,
};

// A prefix to identify a custom policy group.
export const CUSTOM_POLICY_GROUP_PREFIX = 'custom_policies_group';

// Build a single string regex for all of the relative policy type prefixes.
export const POLICY_PREFIX_REGEX = `${CORE_POLICY_PREFIX}|${CUSTOM_POLICY_PREFIX}|${LOCKED_POLICY_PREFIX}|${FEDERAL_POLICY_PREFIX}|${STATE_POLICY_PREFIX}|${POLICY_PREFIX}`;

// A prefix to identify a policy group.
export const POLICY_GROUP_PREFIX = 'policy_group___';

export const HANDBOOK_REORDER_SAVE =
    'Your changes have not been saved. Do you want to stay on the page so that you can save your changes?';

export const HANDBOOK_RESTORE_TABLE_OF_CONTENTS = 'Restore Table of Contents';
export const HANDBOOK_REORDER_RESTORE = `Are you sure you want to restore the table of contents to it's default order? This will not affect the enabled or disabled state of a policy or any language changes you may have made`;
export const HANDBOOK_RESTORE_DEFAULT_ORDERS = 'Restore to Default Order';

export const TOC_REORDER_ACTIONS = {
    UNDO: 'undo',
    SAVE: 'save',
    CANCEL: 'cancel',
    RESTORE: 'restore',
};

export const POLICY_ALERT = {
    ACCEPT_MESSAGE:
        'This will replace your current policy language with the policy language provided in this alert.',
    ACCEPT_MESSAGE_DELETED: 'Clicking on Accept will remove this policy from this handbook.',
    ACCEPT_MESSAGE_NEW: 'Accepting this policy update will insert the policy into your handbook.',

    DISMISS_MESSAGE:
        'This will remove the current alert and ignore new policy language. Your current policy will not be modified.',
    DISMISS_MESSAGE_NEW:
        'Dismissing this policy update will insert the policy into your handbook however the policy will not be activated.',

    CONFIRM_ACCEPT_MESSAGE:
        'Accepting this policy update may overwrite any custom content on this policy.',
    CONFIRM_ACCEPT_MESSAGE_DELETED:
        'Accepting this policy update will delete this policy from your handbook.',
    CONFIRM_ACCEPT_MESSAGE_NEW:
        'Accepting this policy update will insert the policy into your handbook.',
    CONFIRM_ACCEPT_TITLE: 'Policy Update',

    CONFIRM_DISMISS_DESCRIPTION:
        'Dismissing this update may bring your handbook out of compliance.',
    CONFIRM_DISMISS_DESCRIPTION_TEMPLATE:
        'Dismissing this update will deactivate the policy and pause its linkage to the associated handbooks.',
    CONFIRM_DISMISS_TITLE: 'Dismiss Policy Alert',

    MESSAGE_HEADING: 'This policy may not be modified until you accept or dismiss this alert',
    MESSAGE_HEADING_DELETED:
        'This policy will be deleted upon Accepting this alert. Please copy the Previous Policy Language separately if you wish to save it',
    MESSAGE_HEADING_LOCKED: 'This policy may not be modified until you accept this alert',

    POLICY_ENABLED_TOOLTIP:
        'The status cannot be changed before accepting or dismissing the policy alert',
    POLICY_ALERT_COMMENT_DEFAULT: 'This policy has been updated.',

    VIEW_TEXT_DIFF_CHANGED_LABEL: 'Updated Policy Language',
    VIEW_TEXT_DIFF_ORIGINAL_LABEL: 'Previous Policy Language',
    VIEW_TEXT_DIFF_TITLE: 'Text Diff for Policy Alert',
};

export const POLICY_MOVED_FROM_STATE_TO_CORE =
    'You have moved a ${stateName} policy to the Core section of your handbook. The downloaded handbook will now display this policy in the Core section. By making this change, you accept the risk that the policy language is applicable and compliant with the laws of the other states included in your handbook. You will need to remember that this is still considered a ${stateName} policy for purposes of the builder. As a result, alerts will be generated, and the policy will be updated, similar to any other ${stateName} policy.';

export const POLICY_TYPES = {
    POLICY_CUSTOM: 'custom',
    POLICY_CORE: 'core',
    POLICY_FEDERAL: 'federal',
    POLICY_STATE: 'state',
    SPANISH_KEY: 'handbooks.spanish',
};

export const POLICY_EDIT = {
    SPECIAL_NOTE: 'Special Note',
    POLICY_LANGUAGE: 'Policy Language',
    POLICY_INSTRUCTION: 'Policy Instruction',
};

export const PEO_EDITOR = `This policy is totally or partially locked. In some locked policies
you may encounter orange text surrounded by double hard brackets [[
<span style="color: #f79232">text</span>]]. In those cases,
only the orange content within those brackets is capable of being
edited. If you remove all the language that is contained within
those brackets, the brackets will still appear in the Text Editor
but will not appear in the policy when downloaded. Click Preview
Policy" to see how the policy will appear when downloaded.
`;

export const CAPTION_FOR_POLICY_SAVE = `If you have changed the language in this policy, note that this may
                                    bring the policy out of compliance with federal or state law. By
                                    making changes to this policy or replacing it with your own policy,
                                    you assume the risk that the policy may no longer be in compliance
                                    with the law.`;

export const EDITOR_CHANGE_TYPE = [
    'adjust-depth',
    'apply-entity',
    'backspace-character',
    'change-block-data',
    'change-block-type',
    'change-inline-style',
    'move-block',
    'delete-character',
    'insert-characters',
    'insert-fragment',
    'redo',
    'remove-range',
    'spellcheck-change',
    'split-block',
    'undo',
    'insert-link',
    'replace-text',
];

export const SUMMARY_OF_CHANGES = {
    BUTTON_LABEL: 'Summary of Changes',
    REDIRECT_TO: 'summary',
    ACCEPT_ALL_CHANGES_LABEL: 'Accept All Changes',
    ACCEPT_BUTTON_MESSAGE:
        'By clicking "Accept," changes will be made to the identified policies as outlined in the summary of changes. Additionally, any policies with outstanding policy alerts notifying you about a policy deletion will be removed. Any custom language contained in the identified policies will be lost. There is no way to undo these changes once accepted. It is recommended that, prior to making any changes to the Handbook Settings page, you address any outstanding alerts in the handbook and save any policy language that you want to keep.',
    CONFIRM_ACCEPT_TITLE: 'Caution! Accepting All Changes',
    CONFIRM_ACCEPT_MESSAGE: `Accepting changes may overwrite custom content in this handbook's existing policies. There is no 'Undo' after changes are applied.`,
    CONFIRM_DISMISS_MESSAGE:
        'No changes will be made to this handbook and the previous number of employees will be reinstated.',
    CONFIRM_ACCEPT_MESSAGE_LINKED: 'All these changes will be applied to your handbook.',
    CONFIRM_DISMISS_MESSAGE_LINKED:
        'No changes will be made to this handbook and states will be reinstated.',
    CONFIRM_DISMISS_TITLE: 'Caution! Dismissing All Changes',
    DISMISS_ALL_CHANGES_LABEL: 'Dismiss All Changes',
    DISMISS_BUTTON_MESSAGE:
        'By clicking "Dismiss," no changes will be made to the identified policies and the change(s) made to your selected states and/or number of employees on the Handbook Settings page will be rolled back. Failure to accept the recommended changes may result in the identified policies being noncompliant with federal or state law. By failing to accept the changes, you assume the risk that the identified policies may no longer be legally compliant.',

    HANDBOOK_SETTINGS_WARNING:
        'Due to the change(s) made to your selected states and/or number of employees, it is necessary that some policies be added, replaced, and/or removed. These policies are identified in the summary of changes below along with a description of the recommended change.',
    HANDBOOK_TEMPLATES_SETTINGS_WARNING:
        'Due to the change(s) made to your selected states, it is necessary that some policies be added, replaced, and/or removed. These policies are identified in the summary of changes below along with a description of the recommended change.',
    NO_CHANGES_BANNER_MESSAGE:
        'The employee count changes do not require any handbook policies to be added and/or removed.',
    NO_CHANGES_BANNER_TITLE: 'NO POLICY CHANGES REQUIRED',
    NO_CHANGES_BUTTON_MESSAGE:
        'Policies in this handbook WILL NOT be effected by the settings change.',
    LOCAL_POLICY_ADDED_LABEL: 'Added',
    LOCAL_POLICY_REMOVED_LABEL: 'Removed',
    LOCAL_POLICY_ACCEPT_BUTTON_MESSAGE:
        'By clicking "Accept," the policies outlined in the Summary of Changes will be added to the Table of Contents. You must accept these policies in order to continue using this handbook, however you are not required to include them in your handbook. The policies will be added in a disabled state, and will not be incorporated into your downloaded handbook unless you enable each policy via the corresponding toggle switch.',
    LOCAL_TEMPLATE_POLICY_ACCEPT_BUTTON_MESSAGE:
        'By clicking "Accept," the updates outlined in the Summary of Changes will be implemented in your handbook. You must accept these changes in order to continue using this handbook.',
    LOCAL_TEMPLATE_POLICY_CONFIRM_ACCEPT_MESSAGE:
        'Accepting changes will update your handbook. You must accept these changes to continue using this handbook.',
    LOCAL_POLICY_REMOVE_BUTTON_MESSAGE:
        'By clicking "Accept," the policies outlined in the Summary of Changes will be removed from your handbook. You must accept these changes in order to continue using this handbook.',
    LOCAL_POLICY_CONFIRM_ACCEPT_MESSAGE: `Accepting changes will add the indicated Local Policies to your handbook in a disabled state. You must accept these policies in order to continue using this handbook.`,
    LOCAL_POLICY_CONFIRM_REMOVE_MESSAGE: `Accepting changes will remove all Local Policies from your handbook. Any custom changes you have made to these policies will be lost.`,
    STATES_REMOVED_BANNER_TITLE: 'STATES REMOVED FROM HANDBOOK',
    STATES_REMOVED_BANNER_MESSAGE:
        'The following states and the policies associated with each state will be removed. Once accepted, any custom language contained in these state policies will be permanently lost and cannot be recovered.',
    POLICY_LEGEND: {
        ADDED: 'Policy Will Be Added',
        REMOVED: 'Policy Will Be Removed',
        PREVIEW: 'Preview Policy',
        ENABLED: 'Enabled Policy',
        DISABLED: 'Disabled Policy',
        UPDATED_POLICY: 'Updated Policy',
        NEW_POLICY: 'New Policy',
        REQUIRED_POLICY: 'Required Policy',
        CAN_EDIT: 'Editing Enabled',
        CANNOT_EDIT: 'Editing Disabled',
    },
    ACTION_BADGE_TEXT: {
        ADDED: 'ADDED',
        REMOVED: 'REMOVED',
        PREVIEW: 'PREVIEW',
        ENABLED: 'ENABLED',
        DISABLED: 'DISABLED',
        UPDATED: 'UPDATED',
        NEW: 'NEW',
        REQUIRED: 'REQUIRED',
        NEW_POLICY: 'NEW POLICY',
        CAN_EDIT: 'EDITING ENABLED',
        CANNOT_EDIT: 'EDITING DISABLED',
        LINKED: 'LINKED',
        UNLINKED: 'UNLINKED',
        DELETED: 'DELETED',
    },
};

export const HANDBOOK_ICONS = {
    POLICY: faGavel,
    RELOAD: faRedo,
    TRASH: faTrash,
    PDF: faFilePdf,
    DOWNLOAD: faDownload,
    ES: faCommentDots,
    CHECK: faCheck,
    TICKET: faBars,
    COPY: faClone,
    RESET: faSync,
    DENIED: faBan,
    LINK: faLink,
    UNLINK: faUnlink,
    NOTIFY_ON: faBell,
    NOTIFY_OFF: faBellSlash,
    SETTINGS: faWrench,
    UPLOAD: faUpload,
    DOCUMENT: faClipboardList,
    WORD: faFileWord,
    MAGIC: faMagic,
    FILE: faFileAlt,
    MAIL: faEnvelope,
};

export const ACTIVITIES = {
    handbook_accept: {
        title: 'Summary of Changes Accepted',
        text:
            'changed employee count from [EMPLOYEECOUNTBEFORE] to [EMPLOYEECOUNTAFTER] and accepted summary of changes',
        textTemplate: 'changed states and accepted summary of changes',
        icon_index: HANDBOOK_ICONS.DOCUMENT,
        icon_top_index: HANDBOOK_ICONS.CHECK,
        color: 'green',
    },
    handbook_create: {
        title: 'Handbook Created:',
        text: 'created',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.MAGIC,
        color: 'green',
    },
    handbook_create_esignature: {
        title: 'Sent for E-Signature',
        text: 'sent [HANDBOOKNAME] for e-Signature',
        icon_index: HANDBOOK_ICONS.TICKET,
        icon_top_index: HANDBOOK_ICONS.MAIL,
        color: 'blue',
    },
    handbook_deep_copy: {
        title: 'Handbook Copied:',
        text: 'copied [COPIED_HANDBOOK] FROM [ORIGIN_HANDBOOK]',
        icon_index: HANDBOOK_ICONS.TICKET,
        icon_top_index: HANDBOOK_ICONS.COPY,
        color: 'blue',
    },
    handbook_delete: {
        title: 'Handbook Deleted',
        text: 'deleted handbook [HANDBOOK_ID]',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.NOTIFY_ON,
        color: 'orange',
    },
    handbook_restore: {
        title: 'Handbook Restored',
        text: 'restored handbook [HANDBOOK_NAME_BEFORE]',
        additionalText: ' as [HANDBOOK_NAME_AFTER]',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.NOTIFY_ON,
        color: 'orange',
    },
    handbook_move: {
        title: 'Handbook Moved',
        text:
            'moved handbook [HANDBOOK_NAME_BEFORE] from the company [COMPANY_NAME_BEFORE] to the company [COMPANY_NAME_AFTER]',
        additionalText: ' as [HANDBOOK_NAME_AFTER]',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.NOTIFY_ON,
        color: 'orange',
    },
    handbook_dismiss: {
        title: 'Summary of Changes Dismissed',
        text:
            'changed employee count from [EMPLOYEECOUNTBEFORE] to [EMPLOYEECOUNTAFTER] and dismissed summary of changes',
        textTemplate: 'changed states and dismissed summary of changes',
        icon_index: HANDBOOK_ICONS.DOCUMENT,
        icon_top_index: HANDBOOK_ICONS.DENIED,
        color: 'red',
    },
    handbook_download: {
        title: 'Handbook Downloaded as [FORMAT]',
        text: 'downloaded',
        icon_index: {
            pdf: HANDBOOK_ICONS.PDF,
            word: HANDBOOK_ICONS.WORD,
        },
        icon_top_index: HANDBOOK_ICONS.DOWNLOAD,
        color: 'orange',
    },
    handbook_policy_core_accept: {
        title: 'Policy Alert Accepted',
        text: 'accepted policy alert',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.NOTIFY_ON,
        color: 'green',
    },
    handbook_policy_core_accept_delete: {
        title: 'Delete Policy Alert Accepted',
        text: 'accepted deleted policy alert',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.NOTIFY_ON,
        color: 'green',
    },
    handbook_policy_core_enable: {
        title: 'Policy Enabled:',
        text: 'enabled Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.CHECK,
        color: 'green',
    },
    handbook_policy_core_disable: {
        title: 'Policy Disabled:',
        text: 'disabled Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.DENIED,
        color: 'red',
    },
    handbook_policy_core_enable_local_editing: {
        title: 'Policy Local Editing Enabled:',
        text: 'enabled Local Editing for Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.CHECK,
        color: 'green',
    },
    handbook_policy_core_disable_local_editing: {
        title: 'Policy Local Editing Disabled:',
        text: 'disabled Local Editing for Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.DENIED,
        color: 'red',
    },
    handbook_policy_core_dismiss: {
        title: 'Policy Alert Dismissed',
        text: 'dismissed policy alert',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.NOTIFY_OFF,
        color: 'red',
    },
    handbook_policy_core_update: {
        title: 'Policy Changed:',
        text: 'updated Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.RELOAD,
        color: 'blue',
    },
    handbook_policy_core_reset: {
        title: 'Reset Default Policy:',
        text: 'reset default policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.RESET,
        color: 'blue',
    },
    handbook_policy_federal_accept: {
        title: 'Policy Alert Accepted',
        text: 'accepted policy alert',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.NOTIFY_ON,
        color: 'green',
    },
    handbook_policy_federal_accept_delete: {
        title: 'Delete Policy Alert Accepted',
        text: 'accepted deleted policy alert',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.NOTIFY_ON,
        color: 'green',
    },
    handbook_policy_federal_enable: {
        title: 'Policy Enabled:',
        text: 'enabled Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.CHECK,
        color: 'green',
    },
    handbook_policy_federal_disable: {
        title: 'Policy Disabled:',
        text: 'disabled Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.DENIED,
        color: 'red',
    },
    handbook_policy_federal_enable_local_editing: {
        title: 'Policy Local Editing Enabled:',
        text: 'enabled Local Editing for Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.CHECK,
        color: 'green',
    },
    handbook_policy_federal_disable_local_editing: {
        title: 'Policy Local Editing Disabled:',
        text: 'disabled Local Editing for Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.DENIED,
        color: 'red',
    },
    handbook_policy_federal_dismiss: {
        title: 'Policy Alert Dismissed',
        text: 'dismissed policy alert',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.NOTIFY_OFF,
        color: 'red',
    },
    handbook_policy_federal_update: {
        title: 'Policy Changed: ',
        text: 'updated Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.RELOAD,
        color: 'blue',
    },
    handbook_policy_federal_reset: {
        title: 'Reset Default Policy:',
        text: 'reset default policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.RESET,
        color: 'blue',
    },
    handbook_policy_state_accept: {
        title: 'Policy Alert Accepted',
        text: 'accepted policy alert',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.NOTIFY_ON,
        color: 'green',
    },
    handbook_policy_state_accept_delete: {
        title: 'Delete Policy Alert Accepted',
        text: 'accepted deleted policy alert',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.NOTIFY_ON,
        color: 'green',
    },
    handbook_policy_state_enable: {
        title: 'Policy Enabled:',
        text: 'enabled Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.CHECK,
        color: 'green',
    },
    handbook_policy_state_disable: {
        title: 'Policy Disabled:',
        text: 'disabled Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.DENIED,
        color: 'red',
    },
    handbook_policy_state_enable_local_editing: {
        title: 'Policy Local Editing Enabled:',
        text: 'enabled Local Editing for Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.CHECK,
        color: 'green',
    },
    handbook_policy_state_disable_local_editing: {
        title: 'Policy Local Editing Disabled:',
        text: 'disabled Local Editing for Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.DENIED,
        color: 'red',
    },
    handbook_policy_state_dismiss: {
        title: 'Policy Alert Dismissed',
        text: 'dismissed policy alert',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.NOTIFY_OFF,
        color: 'red',
    },
    handbook_policy_state_update: {
        title: 'Policy Changed:',
        text: 'updated Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.RELOAD,
        color: 'blue',
    },
    handbook_policy_state_reset: {
        title: 'Reset Default Policy:',
        text: 'reset default policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.RESET,
        color: 'blue',
    },
    handbook_policy_custom_link: {
        title: 'Custom Policy Linked',
        text: 'linked custom Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.LINK,
        color: 'blue',
    },
    handbook_policy_custom_unlink: {
        title: 'Custom Policy Unlinked',
        text: 'unlinked custom Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.UNLINK,
        color: 'red',
    },
    handbook_receipt: {
        title: 'Report of Changes Viewed',
        text: 'downloaded',
        icon_index: HANDBOOK_ICONS.PDF,
        icon_top_index: HANDBOOK_ICONS.DOWNLOAD,
        color: 'blue',
    },
    handbook_update: {
        title: 'Handbook Settings Updated',
        text: 'updated handbook settings.',
        icon_index: HANDBOOK_ICONS.TICKET,
        icon_top_index: HANDBOOK_ICONS.SETTINGS,
        color: 'blue',
    },
    handbook_upload_send_esignature: {
        title: 'E-SIGNATURE : SENT',
        text: 'uploaded [HANDBOOKNAME] and sent [HANDBOOKNAME] for e-Signature',
        icon_index: HANDBOOK_ICONS.TICKET,
        icon_top_index: HANDBOOK_ICONS.MAIL,
        color: 'blue',
    },
    handbook_download_substate: {
        title: 'Handbook Download Individual State',
        text: 'downloaded [HANDBOOKNANE] - [DOWNLOADSTATE][EXTENSION] on [DATE]',
        color: 'orange',
        icon_top_index: HANDBOOK_ICONS.DOWNLOAD,
        icon_index: {
            pdf: HANDBOOK_ICONS.PDF,
            word: HANDBOOK_ICONS.WORD,
        },
    },
    handbook_toc_update: {
        title: 'Table of Contents Reordered',
        text: 're-arranged the order of policies in the table of contents',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.SETTINGS,
        color: 'blue',
    },
    handbook_summary_local_policy_accepted: {
        title: 'Summary of Local Policies Accepted',
        text: 'accepted summary of local policies changes',
        icon_index: HANDBOOK_ICONS.DOCUMENT,
        icon_top_index: HANDBOOK_ICONS.CHECK,
        color: 'green',
    },
    handbook_summary_local_policy_deleted: {
        title: 'Summary of Local Policies Removed',
        text: 'summary of local policies removed changes',
        icon_index: HANDBOOK_ICONS.DOCUMENT,
        icon_top_index: HANDBOOK_ICONS.CHECK,
        color: 'green',
    },
    handbook_policy_custom_update: {
        title: 'Custom Policy Changed:',
        text: 'updated Custom Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.RELOAD,
        color: 'blue',
    },
    handbook_toc_restore: {
        title: 'Table of Contents Restored',
        text: 'table of contents restored',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.RELOAD,
        color: 'blue',
    },
    handbook_accept_master_template_alerts: {
        title: 'Handbook Templates Alerts Accepted',
        text: 'accepted handbook templates alerts',
        icon_index: HANDBOOK_ICONS.DOCUMENT,
        icon_top_index: HANDBOOK_ICONS.CHECK,
        color: 'green',
    },
    handbook_policy_custom_reset: {
        title: 'Reset Default Custom Policy:',
        text: 'reset default custom policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.RESET,
        color: 'blue',
    },
    handbook_policy_custom_enable_local_editing: {
        title: 'Custom Policy Local Editing Enabled:',
        text: 'enabled Local Editing for Custom Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.CHECK,
        color: 'blue',
    },
    handbook_policy_custom_disable_local_editing: {
        title: 'Custom Policy Local Editing Disabled:',
        text: 'disabled Local Editing for Custom Policy Name',
        icon_index: HANDBOOK_ICONS.POLICY,
        icon_top_index: HANDBOOK_ICONS.DENIED,
        color: 'blue',
    },
};

export const HANDBOOK_SETTINGS = {
    handbookName: 'Handbook Name',
    handbookType: 'Handbook Type',
    handbookCompanyName: 'Handbook Company Name',
    handbookCompanyHead: 'Handbook Company Head',
    handbookCompanyHeadTitle: 'Handbook Company Head Title',
    primaryContact: 'Primary Contact',
    companyTerm: 'Company Term',
    employeeTerm: 'Employee Term',
    handbookLogo: 'Handbook Logo',
    isSpanishEnabled: 'Is Spanish Enabled',
    totalEmployeeCount: 'Total Employee Count',
    handbookManagerSupervisorTitle: 'Handbook Manager / Supervisor',
    description: 'Description',
    adminUsers: 'Admin Users',
    handbookPeoNameReference: 'PEO Name or Reference',
};
export const EMPLOYEE_COUNT_MESSAGE = `The Employee counts you entered are different from the number stored in the system. If the count you entered is correct, please update this information on the Settings page for the handbook you are working with.`;

export const STATE_MAX_EMPLOYEE_COUNT = 100000;
export const MIN_EMPLOYEE_COUNT = 1;
export const FEDERAL_MAX_EMPLOYEE_COUNT = 300000;

export const STATE_MAX_EMP_COUNT_ERROR_MESSAGE = 'Max 100,000';
export const STATE_MIN_EMP_COUNT_ERROR_MESSAGE = 'MIN. 1 REQUIRED';

export const FEDERAL_STATE_COUNT_MESSAGE =
    'This handbook requires the total number of employees be entered to add to required compliance policies.';

export const HANDBOOK_DUPLICATE_MESSAGE =
    'Handbook Name already exists. Please choose a different name or use the suggested Handbook Name: -';

export const HANDBOOK_DUPLICATE = 'Duplicate Handbook name found';

export const EMPLOYEE_COUNT_TITLE = 'Total Employee Count';

export const FEDERAL_COUNT_MESSAGE = 'Please enter the total employee count for this company.';
export const FEDERAL_MIN_EMP_COUNT_ERROR_MESSAGE = 'Employee count must be between 1 and 300,000';
export const FEDERAL_MAX_EMP_COUNT_ERROR_MESSAGE = 'Employee count cannot exceed 300,000';

export const HANDBOOK_ACTIONS = {
    LINK_CUSTOM_POLICY: {
        TITLE: 'Link Custom Policy',
    },
    LINK_CUSTOM_POLICY_TEMPLATE: {
        TITLE: 'Link to Company Policy Template',
    },
    HANDBOOK_SETTINGS: {
        TITLE: 'Handbook Settings',
        TITLE_TEMPLATE: 'Template Settings',
    },
    DOWNLOAD_WORD: {
        TITLE: 'Download Word (.docx)',
        DISABLED_TOOLTIP: ALERT_CONTENT,
    },
    DOWNLOAD_SPANISH_WORD: {
        TITLE: 'Download Spanish Word (.docx)',
        DISABLED_TOOLTIP: ALERT_CONTENT,
    },
    DOWNLOAD_PDF: {
        TITLE: 'Download PDF (.pdf)',
        DISABLED_TOOLTIP: ALERT_CONTENT,
    },
    DOWNLOAD_SPANISH_PDF: {
        TITLE: 'Download Spanish PDF (.pdf)',
        DISABLED_TOOLTIP: ALERT_CONTENT,
    },
    DOWNLOAD_INDIVIDUAL_STATE: {
        TITLE: 'Download Individual State',
        DISABLED_TOOLTIP: ALERT_CONTENT,
    },
    SEND_FOR_E_SIGNATURE: {
        TITLE: 'Send for E-Signature',
        DISABLED_TOOLTIP:
            'Policy alerts are present and pending on this handbook. Please accept/dismiss policy alerts through Table of Contents to send this handbook for e-signature.',
        DISABLED_IMPERSONATION_TOOLTIP:
            'Handbook cannot be sent for e-Signature during impersonation',
    },
    VIEW_E_SIGNATURE_DASHBOARD: {
        TITLE: 'View E-Signature Dashboard',
        DISABLED_TOOLTIP: 'You are not allowed to manage documents',
    },
    SEND_RESTORE_HANDBOOK: {
        TITLE: 'Restore Handbook',
        DISABLED_TOOLTIP:
            'The handbook cannot be restored as it was automatically deleted due to recent changes in the client configuration.',
    },
    COPY_HANDBOOK: {
        TITLE: 'Copy Handbook',
    },
    DELETE_HANDBOOK: {
        TITLE: 'Delete Handbook',
        DESCRIPTION: 'Are you sure you want to delete this handbook?',
    },
    MOVE_HANDBOOK: {
        TITLE: 'Move Handbook',
        DISABLED_TOOLTIP: 'You are not allowed to move handbooks',
    },
    VIEW_ACTIVITY_STREAM: {
        TITLE: 'View Activity Stream',
        DISABLED_TOOLTIP: ALERT_CONTENT,
    },
    DOWNLOAD_HANDBOOK: {
        TITLE: 'Download Handbook',
    },
    DOWNLOAD_ASYNC_HANDBOOK: {
        TITLE: 'Your Handbook Is Almost Ready!',
    },
    RESTORE_HANDBOOK: {
        TITLE: 'Restore Handbook',
    },
    REMOVE_HANDBOOK: {
        TITLE: 'Remove Client',
    },
    RESTORE_HANDBOOKS_TEMPLATE: {
        TITLE: 'Restore Handbook Templates',
    },
    TEMPLATE_SETTINGS: {
        TITLE: 'Template Settings',
    },
    LINKED_HANDBOOKS: {
        TITLE: 'Linked Handbooks',
    },
};

export const DOWNLOAD_FILE_MESSAGE = 'Downloading File';
export const SAVE_MODAL_DESCRIPTION = 'If you continue, your changes will be lost.';
export const DELETE_STATE_MESSAGE =
    "Because you're creating a multi-state handbook, you need to specify at least one state or jurisdiction to start with. You can add other states later if you need to.";

export const HANDBOOK_TYPE = {
    TITLE: 'Handbook Type',
    FEDERAL: 'federal',
    MULTI_STATE: 'multistate',
    ONE_STATE: 'onestate',
    UPSELL_MESSAGE: 'upsell',
    SETTING_NAME_HANDBOOK_TYPE: 'handbookType',
};
export const FEDERAL_HANDBOOK_TYPE = {
    FEDERAL_ONLY: 'Federal Only',
    MULTI_STATE: 'Federal + Multi-States',
    ONE_STATE: 'Federal + One-State',
};
export const UPSELL_MESSAGE_TYPE = {
    SMART_EMPLOYEE_HANDBOOK_PLUS_MESSAGE_EMPTY: '',
    SMART_EMPLOYEE_HANDBOOK_PLUS_MESSAGE: 'Upgrade to Smart Employee Handbook Plus',
    SMART_EMPLOYEE_HANDBOOK_PLUS_FOLLOWUP_STATES_MESSAGE: 'Need to add more states?',
};
export const RESTORE_TOC_MESSAGE =
    'Successfully restored the Table of Contents order back to defaults.';
export const HANDBOOK_REORDER_SUCCESS = 'Successfully updated Table of Contents order.';

export const CUSTOM_POLICY = {
    CREATE: {
        TITLE: 'New Custom Policy',
    },
    EDIT: {
        GO_BACK: {
            CANCEL_ACTION_TEXT: 'CANCEL',
            CONTINUE_ACTION_TEXT: 'CONTINUE WITHOUT SAVING',
            TITLE: 'Caution! Unsaved Changes',
        },
    },
    LABEL_NEW: 'New Custom Policy',
    BUTTONS: {
        CANCEL: 'Cancel',
        CONTINUE: 'Continue',
        PREVIEW: 'Preview Policy',
        DEFAULT: 'Default Policy',
        BACK: 'Back To Editor',
        RESTORE: 'Restore to Default',
    },
    RESTORE_LABEL:
        'This action will overwrite any custom policy language and restore the policy to the default language.',
};

export const CUSTOM_POLICY_TEMPLATE = {
    CREATE: {
        TITLE: 'New Custom Policy for Template',
    },
    EDIT: {
        GO_BACK: {
            CANCEL_ACTION_TEXT: 'CANCEL',
            CONTINUE_ACTION_TEXT: 'CONTINUE WITHOUT SAVING',
            TITLE: 'Caution! Unsaved Changes',
        },
    },
    LABEL_NEW: 'New Custom Policy for Template',
};

export const CUSTOMPOLICYTEMPLATES_LIST_EMPTY = {
    TITLE: 'You have no Custom Policy Templates created.',
    DESCRIPTION: 'Create a new Custom Policy for Template',
    TYPE: 'customPolicyTemplate',
};

export const HANDBOOK_LOGO = {
    INVALID_TYPE: 'File type must be (JPG | JPEG | PNG | GIF)',
    INVALID_SIZE: 'File size exceeds 500KB',
};

export const OL_TAG_START_ATTRIBUTE_REGEX = /<ol\s+start=["\d+"]*>/g;

export const MARKDOWN_REGEX = [
    {
        regex: /\\-/g,
        replace: '-',
    },
    {
        regex: /\\_/g,
        replace: '_',
    },
    {
        regex: /\\\*/g,
        replace: '*',
    },
    {
        regex: /\\~/g,
        replace: '~',
    },
    {
        regex: /\\\|/g,
        replace: '|',
    },
    {
        regex: /\\#/g,
        replace: '#',
    },
    {
        regex: /\\\\/g,
        replace: '\\',
    },
    {
        regex: /\\`/g,
        replace: '`',
    },
    {
        regex: /\\\+/g,
        replace: '+',
    },
    {
        regex: /\\>/g,
        replace: '>',
    },
];

export const HANDBOOKS_DISCLAIMER =
    'The Smart Employee Handbook contains employment policies that are drafted and updated by our licensed, in-house attorneys in consultation with national labor and employment law firm Constangy, Brooks, Smith & Prophete, LLP to help private employers create and maintain legally-compliant handbooks based on federal, state, and some local laws. Policies that may be required for compliance with local laws and specific industries are not included in the handbook builder. Policies regarding specific industries are not included. Employers with specific circumstances, such as federal or state contractors/subcontractors and public or unionized employers, should review the policies with legal counsel to ensure applicability and determine if additional policies are needed for their organizations. Employers that use arbitration agreements should also have their handbook reviewed by legal counsel to ensure that provisions of the handbook do not conflict with the terms of their arbitration agreement. Changes made to any policy in the builder could cause the policy to be out of compliance and should be reviewed with legal counsel prior to implementation.<br/><br/>For users exercising the option to have their completed handbooks translated into Spanish, please note that the builder uses artificial intelligence powered by Google Translate to perform some translations. Reasonable efforts have been made to provide accurate translations; however, no automated translation is perfect, nor is it intended to replace human translators. Translations are provided as a service to users of this builder and are provided “as is.” No warranty of any kind, either express or implied, is made as to the accuracy, reliability, or correctness of any translations. Any person or entity that relies on the translations provided by this builder does so at their own risk. Mineral is not liable for any damages or losses of any kind caused by the use of these translations. The official versions of the handbook policies in this builder are in English. If you have questions about the accuracy of the information contained in a translated policy, refer to the English version of the policy.<br/><br/>The builder does not cover state-specific consumer privacy laws, including but not limited to the California Consumer Privacy Act or the California Privacy Rights Act, which may require specific additional disclosures to be made to employees and job applicants, among other things. Employers must consult with legal counsel in the state(s) where they are doing business regarding compliance with state consumer privacy laws, as Mineral will not be responsible regarding your compliance with these laws. We provide no advice whatsoever with respect to state consumer privacy laws, and disclaim all related liability.';

export const HANDBOOK_ALERT_DISMISSED_MSG =
    'The default language for this policy has been updated. View the default policy to insert the updated language into your handbook. Keeping the current language may bring your handbook out of compliance.';

export const HANDBOOK_GUIDES_CONTENTFUL_IDS = {
    HANDBOOKS_CHECKLIST: 'yT4K7ZxGmbFHbUkOAgPjsr',
    HANDBOOKS_GUIDE: 'eSaKaUqydk7TJd0HRGHsyu',
    UPDATE_HANDBOOKS: 'bX9fs535IQU5GM68zgcOHr',
    COMMOM_HANDBOOKS_ISSUES: 'DxHNPk9dHLYjO9Al8CDeBC',
};

export const HANDBOOK_UNLINK_CUSTOMPOLICY = {
    TITLE: 'Unlink Policy',
    DESCRIPTION: 'Are you sure you want to unlink this custom policy?',
};

export const HANDBOOKS_LIST_EMPTY = {
    TITLE: 'You have no Handbooks created.',
    TYPE: 'handbook',
    DESCRIPTION: 'Create a new Handbook',
};

export const CUSTOMPOLICIES_LIST_EMPTY = {
    TITLE: 'You have no Custom Policies created.',
    DESCRIPTION: 'Create a new Custom Policy',
    TYPE: 'customPolicy',
};

export const POLICY_TOGGLE = {
    ENABLED: 'Policy Enabled',
    DISABLED: 'Policy Disabled',
    TEMPLATE_ENABLED: 'Client Editing Enabled',
    TEMPLATE_DISABLED: 'Client Editing Disabled',
};

export const HANDBOOK_KEY = 'handbooks';
export const PEO_HANDBOOK_KEY = 'peohandbook';
export const LOCAL_POLICY_KEY = 'handbookslocalpolicies';
export const HANDBOOK_LABEL = 'Handbooks';
export const ARCHIVED_HANDBOOK_LABEL = 'Archived Handbooks';
export const DELETED_HANDBOOK_LABEL = 'Deleted Handbooks';
export const MOVE_HANDBOOK_LABEL = 'Move Handbooks';
export const HANDBOOK_LABELS_MENU = {
    ARCHIVED_HANDBOOK_LABEL: 'Archived',
    DELETED_HANDBOOK_LABEL: 'Deleted',
    MOVE_HANDBOOK_LABEL: 'Moved',
};
export const HANDBOOKS_TEMPLATE_LABEL = 'Handbook Templates';
export const DELETED_HANDBOOKS_TEMPLATE_LABEL = 'Deleted Handbook Templates';
export const HANDBOOKS_TEMPLATE_NAME = 'Template Name';
export const HANDBOOKS_TEMPLATE_SEARCH = 'Search Deleted Handbook Templates';
export const HANDBOOKS_TEMPLATE_NO_DATA = 'No templates found';
export const CREATE_MENU = {
    HANDBOOK: 'New Handbook',
    POLICY: 'New Custom Policy',
    TEMPLATE: 'New Handbook Template',
    POLICY_TEMPLATE: 'New Custom Policy for Template',
};
export const POLICY_ATTACHMENTS = 'Policy Attachments';

export const MAX_DIFF_CHARACTERS = 40000;
export const SPLIT_DIFF_CHARACTERS = 500;
export const POLICY_DETAILS_FIELDS = {
    ATTACHMENTS: 'Attachments',
    CREATED: 'Created',
    STATUS: 'Status',
    UPDATED: 'Last Updated',
    PUBLISH_STATUS: 'Publish Status',
    PUBLISHED: 'Published',
    NOT_PUBLISHED: 'Not Published',
};
export const HANDBOOK_POLICY = {
    DESCRIPTION: 'You have unsaved changes',
    ACCEPT: 'Click here to recover them',
    DISMISS: 'Dismiss the changes',
};
export const DISCARD_EDITS = 'Discard';
export const SAVE_EDITS = 'Save';
export const SAVE_POLICY = 'Save Policy';
export const SMART_EMPLOYEE_HANDBOOK_PLUS = {
    TITLE: 'Smart Employee Handbook Plus',
    DESCRIPTION: '<b>Upgrade</b> for more features including Spanish Translation and E-Signature',
};
export const ROLE_CLIENT_ADMIN = 'Client Admin';
export const GTM_MOODEL_VALUE_ADD = 'Value Add';
export const HANDBOOK_SKU = {
    SPANISH: 'spanish',
};

export const HANDBOOK_LABELS = {
    LOCKED: 'Locked',
    REQUIRES_CUSTOMIZATION: 'Requires Customization',
    LINKED_REQUIRES_CUSTOMIZATION: 'Limited Editing',
    CORE_POLICY: 'Core Policy',
    CHANGES_NOT_PUBLISHED: 'Changes Not Published',
    ERROR_MESSAGE_LIST_AFFECTED_HANDBOOKS: 'Unable to fetch affected handbooks.',
};

export const HANDBOOK_MOVE = {
    label: 'Move Handbook',
    TEXT:
        'Here you can move a handbook. Select the company to which you want to move the handbook.',
};

export const POLICY_EXCEEDS_BULLET_LIMIT =
    'Please note that the content includes lists with more than 9 levels. Please restructure the lists to ensure they do not exceed the limit: 9.';

export const TOTAL_EMPLOYEES = 'TOTAL EMPLOYEES';

export const ADVANCED_SEARCH_TITLE = 'Advanced Search for Handbooks';
export const ADVANCED_SEARCH_TOC_TITLE = 'Advanced Search for Policies';

export const ADVANCED_SEARCH_DESCRIPTION =
    'Select from our filters to customize your search results.';

export const HANDBOOKS_SEARCH = {
    HANDBOOK_COUNT_MIN_LIMIT: 1,
    HANDBOOK_COUNT_MAX_LIMIT: 300000,
    HANDBOOK_COUNT_NUMBER_MESSAGE: 'Total head count must be a number.',
    HANDBOOK_TOTAL_HEAD_COUNT_MESSAGE: 'Total head count must be between 1 to 300000',
};
export const BACK_TO_TOC = 'Back to Table of Contents';
export const POLICY_ACTIONS = {
    EDIT: 'Edit',
    PREVIEW: 'Preview',
    DETAILS: 'Details',
    UNLINK: 'Unlink',
    TEMPLATE_TOOLTIP:
        "By activating this option, this policy won't be managed from the template and the client admins will be able to customize it.",
    TEMPLATE_POLICY_CUSTOMIZATION: 'Policy Customization',
    TEMPLATE_ENABLE_POLICY_CUSTOMIZATION:
        'By enabling this option, client admins will be able to customize this policy while it continues to receive updates from the handbook templates.',
    TEMPLATE_DISABLE_POLICY_CUSTOMIZATION:
        'By disabling this option, this policy will once again be managed from the template. Client admins will no longer be able to customize it.',
};
export const DEFAULT_SMART_HANDBOOK_OPTIONS = {
    SMART_HANDBOOK: {
        TITLE: 'Smart Employee Handbook Plus',
        DISCLAIMER_TEXT:
            'Build a handbook for your company and all states with at least one company location.',
        NOTE: '<b>Pricing is based on company size.</b>',
        PENDO_ID: 'IPFM-Proceed_with_Multi-State',
        DISCLAIMER_LINK: 'No, thanks',
    },
    BUTTONS: {
        LEARN_MORE: 'Get Pricing',
        CANCEL: 'No, thanks',
    },
    SEH_SKU_LIST: {
        multistate: [
            {
                id: 1,
                name: 'Federal Compliance',
            },
            {
                id: 2,
                name: 'Multi-State Compliance',
            },
            {
                id: 3,
                name: 'Manage Employee E-Signatures',
            },
            {
                id: 4,
                name: 'Spanish Translation',
            },
            {
                id: 5,
                name: 'Select Local Policies',
            },
        ],
        spanish: [
            {
                id: 1,
                name: 'Federal Compliance',
            },
            {
                id: 2,
                name: 'Multi-State Compliance',
            },
            {
                id: 3,
                name: 'Manage Employee E-Signatures',
            },
            {
                id: 4,
                name: 'Spanish Translation',
            },
            {
                id: 5,
                name: 'Select Local Policies',
            },
        ],
    },
};

export const CHECK_STATUS_TIME = 10000;
export const CHECK_MAX_STATUS_TIME = 120000;

export const HANDBOOK_TEMPLATE = 'Handbook Template';
export const MANAGED_BY_PARTNER = 'Managed by Partner';

export const FILTER_RESET = 'Reset';
export const HANDBOOKS_CHOOSE_COMPANY_PATH = '/company-policies/handbooks/choose-company';

export const LINKED_HANDBOOKS = {
    SINGULAR: 'Linked Handbook',
    PLURAL: 'Linked Handbooks',
    NO: 'No Linked Handbooks',
    SELECT_A_COMPANY: 'Select a Company',
    ADD_NEW: 'Create New Linked Handbook',
    SEARCH_COMPANY: 'Search company',
    NO_COMPANIES_FOUND: 'No companies found',
    FAILED_TO_LOAD_CLIENTS: 'Failed to load clients',
    LOADING: 'Loading...',
};
export const MASTER_TEMPLATES_PATH = '/company-policies/handbooks/templates/list';
export const HANDBOOK_CREATE_SCHEMA_ORDER = [
    'companyName',
    'companyLogo',
    'referCompany',
    'referEmployee',
    'headOfCompany',
    'headOfCompanyTitle',
    'primaryContact',
    'howRefer',
    'helpText',
];

export const FEDERAL_TYPE = {
    LABEL: 'Federal',
    VALUE: 'FED',
};
export const NEW_HANDBOOK_LABEL = 'New Handbook';
export const PUBLISH_ALL_CHANGES_DESCRIPTION =
    "You're about to publish all saved changes applied to your policies.<br>All linked handbooks will get these updates.";
export const PUBLISH_ALL_CHANGES_SUCCESS = 'All changes have been published successfully.';
export const CONFIRM_SEND_REMINDERS_ADMINS_TO_PUBLISH =
    'Are you sure you want to send reminder emails to all handbook administrators to publish pending changes?';
export const TOOLTIPS_EDIT_POLICY = {
    DISABLED_TEMPLATE:
        'Managed from the template. This will disable / enable the policy in all handbooks.',
    DISABLED_LINKED: 'This will disable / enable the policy.',
    LOCAL:
        'Allow customers to make local edits to this policy in their own handbook without affecting the original template.',
    PUBLISH_CHANGES: 'Publish changes to this policy and update all linked handbooks.',
    SAVE_AS_DRAFT: 'Save the policy without publishing it. Linked handbooks will not be updated.',
    VIEW_AFFECTED_HANDBOOKS:
        'View the handbooks that will be affected by publishing changes to this policy.',
};
export const POLICY_ALERTS_MANAGED_BY_PARTNER =
    'The following changes have been made to your handbook.';
export const POLICY_ALERTS_MANAGED_BY_PARTNER_TITLE = 'Handbook Template Updated';
export const DISABLED_LOCAL_EDIT_LABEL = 'Editing Disabled';
export const CHANGES_NOT_PUBLISHED = {
    BUTTON_LABEL: 'Save as Draft',
    TOOLTIP: 'Save the policy without publishing it. Linked handbooks will not be updated.',
};
export const BUTTONS_LABELS = {
    SAVE_AS_DRAFT: 'Save as Draft',
    PUBLISH: 'Publish',
    VIEW_AFFECTED_HANDBOOKS: 'View Affected Handbooks',
    SAVE_POLICY: 'Save Policy',
};
